import React, { useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { Box, Typography, Button, CircularProgress, Alert } from '@mui/joy'
import { ArrowLeft as ArrowBack } from '@phosphor-icons/react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { DocumentForm } from '../components/billing'
import { estimatesApi, taxRatesApi } from '../lib/billingApi'
import { apiClient } from '../lib/api'
import type { TaxRate } from '../types/billing'

export default function EstimateEdit() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Fetch estimate data
  const { data: estimate, isLoading: estimateLoading, error: estimateError } = useQuery({
    queryKey: ['estimate', id],
    queryFn: () => estimatesApi.get(parseInt(id!)),
    enabled: !!id
  })

  // Fetch customers
  const { data: customersData, isLoading: customersLoading } = useQuery({
    queryKey: ['contacts'],
    queryFn: async () => {
      const response = await apiClient.getContacts({ page_size: 1000 })
      // Handle paginated response structure
      const data = response.data as any
      return data?.results || data
    }
  })

  // Fetch jobs (include completed so estimates can link after close-out)
  const { data: jobsData, isLoading: jobsLoading } = useQuery({
    queryKey: ['jobs', { includeCompleted: true, pageSize: 1000 }],
    queryFn: async () => {
      const response = await apiClient.getJobs(undefined, true, 1000)
      return response.data
    }
  })

  // Fetch opportunities
  const { data: opportunitiesData, isLoading: opportunitiesLoading } = useQuery({
    queryKey: ['opportunities'],
    queryFn: async () => {
      try {
        const response = await apiClient.getOpportunities()
        // Ensure we always return an array
        return Array.isArray(response.data) ? response.data : []
      } catch (error) {
        console.error('Failed to fetch opportunities:', error)
        // Return empty array on error to prevent crashes
        return []
      }
    },
    retry: false, // Don't retry on failure
    staleTime: 5 * 60 * 1000 // Consider data stale after 5 minutes
  })

  // Fetch tax rates
  const { data: taxRates = [], isLoading: taxRatesLoading } = useQuery<TaxRate[]>({
    queryKey: ['taxRates'],
    queryFn: async () => {
      const response = await taxRatesApi.list()
      return response.results
    }
  })

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: any) => estimatesApi.update(parseInt(id!), data),
    onSuccess: () => {
      toast.success('Estimate updated successfully')
      queryClient.invalidateQueries({ queryKey: ['estimate', id] })
      queryClient.invalidateQueries({ queryKey: ['estimates'] })
      navigate(`/operations/billing/estimates/${id}`)
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to update estimate')
      setIsSubmitting(false)
    }
  })

  const handleSubmit = async (data: any) => {
    setIsSubmitting(true)
    
    // Prepare the data for PATCH request
    const updateData = {
      customer: data.customer,
      job: data.job || null,
      opportunity: data.opportunity || null,
      document_date: data.document_date,
      due_date: data.due_date || null,
      valid_until: data.valid_until || null,
      payment_terms: data.payment_terms,
      custom_payment_terms: data.custom_payment_terms || '',
      terms_and_conditions: data.terms_and_conditions || '',
      notes: data.notes || '',
      internal_notes: data.internal_notes || '',
      discount_amount: data.discount_amount || 0,
      // Overhead and profit fields
      include_overhead: data.include_overhead || false,
      overhead_percentage: data.overhead_percentage || null,
      include_profit: data.include_profit || false,
      profit_percentage: data.profit_percentage || null,
      // Note: Line items should be updated through a separate endpoint
      // For now, we'll include them in the update
      line_items: data.line_items
    }

    try {
      await updateMutation.mutateAsync(updateData)
    } catch (error) {
      throw error // Keep the form draft and show server validation fields.
    }
  }

  const handleCancel = () => {
    navigate(`/operations/billing/estimates/${id}`)
  }

  // Don't wait for opportunities since they're optional
  const isLoading = estimateLoading || customersLoading || jobsLoading || taxRatesLoading

  if (isLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (estimateError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert color="danger">
          Failed to load estimate: {estimateError.message}
        </Alert>
        <Button
          component={Link}
          to="/operations/billing/estimates"
          startDecorator={<ArrowBack />}
          variant="plain"
          sx={{ mt: 2 }}
        >
          Back to Estimates
        </Button>
      </Box>
    )
  }

  if (!estimate) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert color="warning">
          Estimate not found
        </Alert>
        <Button
          component={Link}
          to="/operations/billing/estimates"
          startDecorator={<ArrowBack />}
          variant="plain"
          sx={{ mt: 2 }}
        >
          Back to Estimates
        </Button>
      </Box>
    )
  }

  // Check if estimate can be edited
  if ((estimate as any).status === 'accepted' || (estimate as any).status === 'rejected' || (estimate as any).status === 'void' || (estimate as any).status === 'cancelled') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert color="warning">
          This estimate cannot be edited because it has been {(estimate as any).status}.
        </Alert>
        <Button
          component={Link}
          to={`/operations/billing/estimates/${id}`}
          startDecorator={<ArrowBack />}
          variant="plain"
          sx={{ mt: 2 }}
        >
          Back to Estimate
        </Button>
      </Box>
    )
  }

  // Ensure customers, jobs, and opportunities are arrays
  const customers = Array.isArray(customersData) ? customersData : []
  const jobs = Array.isArray(jobsData) ? jobsData : []
  const opportunities = Array.isArray(opportunitiesData) ? opportunitiesData : []

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Button
          component={Link}
          to={`/operations/billing/estimates/${id}`}
          startDecorator={<ArrowBack />}
          variant="plain"
          size="sm"
          sx={{ mb: 2 }}
        >
          Back to Estimate
        </Button>
        
        <Typography level="h1">
          Edit Estimate {(estimate as any).document_number}
        </Typography>
      </Box>
      
      <DocumentForm
        document={estimate as any}
        documentType="estimate"
        customers={customers}
        jobs={jobs}
        opportunities={opportunities}
        taxRates={taxRates}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isLoading={isSubmitting}
      />
    </Box>
  )
}